import React, { useEffect, useState, useRef } from "react";
import "./Dashboard.css";

interface Subject {
  title: string;
  subject: string;
  subject_id: string;
  image: string;
  duration: string;
  progress: {
    student_progress: number;
    progress: number;
  };
}

interface SubjectsData {
  subjects: Subject[];
}

interface SubjectsProps {
  subjectsData: SubjectsData | null;
  subjectsLoading: boolean;
}

const Subjects: React.FC<SubjectsProps> = ({ subjectsData, subjectsLoading }) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [scrollPosition, setScrollPosition] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (subjectsData && subjectsData.subjects && subjectsData.subjects.length > 0) {
      const availableColors = ["#B6BAFE","#F0DC54","#B5FEB5","#DB7B7BFF","#B6BAFE","#FFB5B5"];
      
      const subjectsWithColors = subjectsData.subjects.map((subject: Subject, index: number) => ({
        ...subject,
        color: availableColors[index % availableColors.length],
      }));

      setSubjects(subjectsWithColors);
      setTimeout(checkScrollStatus, 100);
    } else {
      setSubjects([]);
    }
  }, [subjectsData]);

  const checkScrollStatus = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      const hasRightScroll = Math.ceil(scrollLeft + clientWidth) < scrollWidth - 2;

      setScrollPosition({
        canScrollLeft: scrollLeft > 0,
        canScrollRight: hasRightScroll
      });
    }
  };

  useEffect(() => {
    checkScrollStatus();

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollStatus);
      setTimeout(checkScrollStatus, 100);
      window.addEventListener('resize', checkScrollStatus);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollStatus);
      }
      window.removeEventListener('resize', checkScrollStatus);
    };
  }, [subjects.length]);

  const scrollLeft = () => {
    if (scrollContainerRef.current && scrollPosition.canScrollLeft) {
      const container = scrollContainerRef.current;
      container.scrollBy({
        left: -container.clientWidth / 2,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current && scrollPosition.canScrollRight) {
      const container = scrollContainerRef.current;
      container.scrollBy({
        left: container.clientWidth / 2,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="courses-section">
      <div className="courses-header">
        <h3>Subjects</h3>
        <div className="courses-nav">
          <button 
            className="nav-arrow"
            onClick={scrollLeft}
            disabled={!scrollPosition.canScrollLeft}
            style={{
              cursor: scrollPosition.canScrollLeft ? "pointer" : "not-allowed",
              opacity: scrollPosition.canScrollLeft ? 1 : 0.5,
            }}
          >
            &lt;
          </button>
          <button 
            className="nav-arrow"
            onClick={scrollRight}
            disabled={!scrollPosition.canScrollRight}
            style={{
              cursor: scrollPosition.canScrollRight ? "pointer" : "not-allowed",
              opacity: scrollPosition.canScrollRight ? 1 : 0.5,
            }}
          >
            &gt;
          </button>
        </div>
      </div>
      <div 
        className="course-cards"
        ref={scrollContainerRef}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          display: "flex",
          gap: "1rem",
        }}
      >
        {subjectsLoading ? (
          <div>Loading subjects...</div>
        ) : subjects && subjects.length > 0 ? (
          subjects.map((subject) => (
            <div key={subject.subject_id} className="course-card">
              <div className="course-image">
                <img src={subject.image} alt={subject.title} />
              </div>
              <div className="course-content">
                <div className="course-header">
                  <div
                    className="course-label"
                    style={{ backgroundColor: (subject as any).color }}
                  >
                    {subject.title}
                  </div>
                </div>
                <div className="course-duration">
                  <span className="duration-label">Duration</span>
                  <span className="duration-value">
                    {subject.duration}
                  </span>
                </div>
                <div className="course-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${subject.progress.progress}%` }}
                    ></div>
                  </div>
                  {subject.progress.progress === 0 && (
                    <div className="not-started-container">
                      <span className="not-started">Not Started</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-courses-message">
            No subjects found for this course and batch.
          </div>
        )}
      </div>
    </div>
  );
};

export default Subjects;
